// ---------------------------------------------------------------------------
// NotificationBar Renderer (editor key: NotificationBar)
// ---------------------------------------------------------------------------
// Top-of-page banner used for announcements, promo codes, and short CTAs.
// Static markup with variant-driven class hooks (info | success | warning |
// promo). The optional link uses our hardened `url()` escape — anything that
// isn't http(s)/mailto/tel is replaced with `#`, so `javascript:` payloads
// can't escape into the page. `dismissible` controls whether the close
// affordance is shown; we render it as a `<button>` for keyboard access but
// keep behavior CSS-only at runtime (no inline JS in published HTML).

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface NotificationBarProps {
  message?: string;
  linkText?: string;
  linkUrl?: string;
  variant?: "info" | "success" | "warning" | "promo";
  dismissible?: boolean | "yes" | "no";
}

const ALLOWED_VARIANTS = new Set(["info", "success", "warning", "promo"]);

function normalizeVariant(value: unknown): string {
  if (typeof value !== "string") return "info";
  return ALLOWED_VARIANTS.has(value) ? value : "info";
}

function isDismissible(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (value === "yes") return true;
  return false;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as NotificationBarProps;

  const message = typeof p.message === "string" ? p.message.trim() : "";
  if (!message) {
    return "<!-- notification-bar missing message -->";
  }

  const linkText = typeof p.linkText === "string" ? p.linkText.trim() : "";
  const linkUrl = typeof p.linkUrl === "string" ? p.linkUrl.trim() : "";
  const variant = normalizeVariant(p.variant);
  const dismissible = isDismissible(p.dismissible);

  const linkHtml = linkText && linkUrl
    ? `\n    <a class="ease-notification-bar__link" href="${url(linkUrl)}">${text(linkText)}</a>`
    : "";

  const dismissHtml = dismissible
    ? `\n    <button type="button" class="ease-notification-bar__dismiss" aria-label="Dismiss notification">&times;</button>`
    : "";

  return `<aside class="ease-notification-bar ease-notification-bar--${attr(variant)}" role="status" data-ease-variant="${attr(variant)}">
  <div class="ease-notification-bar__inner">
    <p class="ease-notification-bar__message">${text(message)}</p>${linkHtml}${dismissHtml}
  </div>
</aside>`;
}

registerRenderer("notification-bar", { render });
